import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  BellIcon,
  BuildingOfficeIcon,
  CalculatorIcon,
  CpuChipIcon,
  CreditCardIcon,
  CubeIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  MegaphoneIcon,
  ReceiptRefundIcon,
  ScaleIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/solid';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { apiService } from '../../services/api';
import { safeStorage } from '../../utils/safeStorage';
import PhoneInput from '../../components/ui/PhoneInput';
import PasswordInput from '../../components/ui/PasswordInput';
import AIProviderSettingsPage from './AIProviderSettingsPage';
import GPSProviderSettingsPage from './GPSProviderSettingsPage';
import InventoryImportSettings from './InventoryImportSettings';

// Settings tree structure matching DealerCenter
const settingsTree = {
  dealership: {
    label: 'Dealership',
    icon: BuildingOfficeIcon,
    children: {
      general: { label: 'General', component: 'DealershipGeneral' },
      website: { label: 'Website', component: 'DealershipWebsite' },
      chatbot: { label: 'AI Chatbot', component: 'DealershipChatbot' },
    },
  },
  inventory: {
    label: 'Inventory',
    icon: CubeIcon,
    children: {
      general: { label: 'General', component: 'InventoryGeneral' },
      appraisal: { label: 'Appraisal', component: 'InventoryAppraisal' },
      reconditioning: { label: 'Reconditioning', component: 'InventoryReconditioning' },
      pricing: { label: 'Pricing', component: 'InventoryPricing' },
      media: { label: 'Media', component: 'InventoryMedia' },
      import: { label: 'Import', component: 'InventoryImport' },
      export: { label: 'Export', component: 'InventoryExport' },
      print: { label: 'Print', component: 'InventoryPrint' },
      partners: { label: 'Partners', component: 'InventoryPartners' },
      notifications: { label: 'Notifications', component: 'InventoryNotifications' },
      warranty: { label: 'Warranty', component: 'InventoryWarranty' },
    },
  },
  customer: {
    label: 'Customer',
    icon: UserGroupIcon,
    children: {
      import: { label: 'Import', component: 'CustomerImport' },
      leadManagement: { label: 'Lead Management', component: 'CustomerLeadManagement' },
      leadAssignment: { label: 'Lead Assignment', component: 'CustomerLeadAssignment' },
      workflow: { label: 'Workflow', component: 'CustomerWorkflow' },
      voiceSmsEmail: { label: 'Voice, SMS and Email', component: 'CustomerVoiceSmsEmail' },
      chat: { label: 'Chat', component: 'CustomerChat' },
      taskManagement: { label: 'Task Management', component: 'CustomerTaskManagement' },
      salesCompliance: { label: 'Sales and Compliance', component: 'CustomerSalesCompliance' },
      notifications: { label: 'Notifications', component: 'CustomerNotifications' },
    },
  },
  deal: {
    label: 'Deal',
    icon: ReceiptRefundIcon,
    children: {
      general: { label: 'General', component: 'DealGeneral' },
      smsEmail: { label: 'SMS and Email', component: 'DealSmsEmail' },
      printESign: { label: 'Print / eSign', component: 'DealPrintESign' },
      partners: { label: 'Partners', component: 'DealPartners' },
      notifications: { label: 'Notifications', component: 'DealNotifications' },
    },
  },
  bhph: {
    label: 'BHPH',
    icon: CreditCardIcon,
    children: {
      general: { label: 'General', component: 'BHPHGeneral' },
      smsEmail: { label: 'SMS and Email', component: 'BHPHSmsEmail' },
      print: { label: 'Print', component: 'BHPHPrint' },
      partners: { label: 'Partners', component: 'BHPHPartners' },
      notifications: { label: 'Notifications', component: 'BHPHNotifications' },
    },
  },
  service: {
    label: 'Service',
    icon: WrenchScrewdriverIcon,
    children: {
      general: { label: 'General', component: 'ServiceGeneral' },
      scheduling: { label: 'Scheduling', component: 'ServiceScheduling' },
      notifications: { label: 'Notifications', component: 'ServiceNotifications' },
    },
  },
  auction: {
    label: 'Auction',
    icon: ScaleIcon,
    children: {
      general: { label: 'General', component: 'AuctionGeneral' },
      bidding: { label: 'Bidding', component: 'AuctionBidding' },
      notifications: { label: 'Notifications', component: 'AuctionNotifications' },
    },
  },
  carzing: {
    label: 'CarZing',
    icon: MegaphoneIcon,
    children: {
      general: { label: 'General', component: 'CarZingGeneral' },
      listings: { label: 'Listings', component: 'CarZingListings' },
      notifications: { label: 'Notifications', component: 'CarZingNotifications' },
    },
  },
  accounting: {
    label: 'Accounting',
    icon: CalculatorIcon,
    children: {
      general: { label: 'General', component: 'AccountingGeneral' },
      reports: { label: 'Reports', component: 'AccountingReports' },
      notifications: { label: 'Notifications', component: 'AccountingNotifications' },
    },
  },
  payments: {
    label: 'Payments',
    icon: CurrencyDollarIcon,
    children: {
      general: { label: 'General', component: 'PaymentsGeneral' },
      processing: { label: 'Processing', component: 'PaymentsProcessing' },
      notifications: { label: 'Notifications', component: 'PaymentsNotifications' },
    },
  },
  allNotifications: {
    label: 'All Notifications',
    icon: BellIcon,
    component: 'AllNotifications',
  },
  ai: {
    label: 'AI Providers',
    icon: CpuChipIcon,
    component: 'AIProviders',
  },
  gps: {
    label: 'GPS Tracking',
    icon: MapPinIcon,
    component: 'GPSProviders',
  },
};

interface DealTaxSettings {
  use_automatic_tax_rates?: boolean;
  state_tax_rate?: number | null;
  county_surcharge_rate?: number | null;
  local_tax_rate?: number | null;
}

// ── Chatbot Settings Panel ─────────────────────────────────────────────────

const CHATBOT_PROVIDERS = [
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai', label: 'OpenAI (GPT-4o)' },
  { value: 'google', label: 'Google (Gemini)' },
  { value: 'mistral', label: 'Mistral' },
];

const PROVIDER_MODELS: Record<string, { value: string; label: string }[]> = {
  anthropic: [
    { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku (fast, cheap)' },
    { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet (smart)' },
    { value: 'claude-opus-4-6', label: 'Claude Opus 4.6 (most capable)' },
  ],
  openai: [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (fast, cheap)' },
    { value: 'gpt-4o', label: 'GPT-4o (smart)' },
  ],
  google: [
    { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash (fast)' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro (smart)' },
  ],
  mistral: [
    { value: 'mistral-small-latest', label: 'Mistral Small (fast)' },
    { value: 'mistral-large-latest', label: 'Mistral Large (smart)' },
  ],
};

interface ChatbotQAPair {
  question: string;
  answer: string;
}

interface ChatbotConfigState {
  is_enabled: boolean;
  provider: string;
  model_name: string;
  has_api_key: boolean;
  system_prompt: string;
  custom_qa: ChatbotQAPair[];
}

const ChatbotSettingsPanel: React.FC = () => {
  const [config, setConfig] = React.useState<ChatbotConfigState | null>(null);
  const [apiKey, setApiKey] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    apiService
      .get<ChatbotConfigState>('/settings/chatbot')
      .then((data) => setConfig(data))
      .catch(() => setError('Failed to load chatbot settings.'));
  }, []);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {
        is_enabled: config.is_enabled,
        provider: config.provider,
        model_name: config.model_name,
        system_prompt: config.system_prompt,
        custom_qa: config.custom_qa,
      };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      const updated = await apiService.post<ChatbotConfigState>('/settings/chatbot', payload);
      setConfig(updated);
      setApiKey('');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addQAPair = () => {
    if (!config) return;
    setConfig({ ...config, custom_qa: [...config.custom_qa, { question: '', answer: '' }] });
  };

  const updateQAPair = (index: number, field: 'question' | 'answer', value: string) => {
    if (!config) return;
    const next = [...config.custom_qa];
    next[index] = { ...next[index], [field]: value };
    setConfig({ ...config, custom_qa: next });
  };

  const removeQAPair = (index: number) => {
    if (!config) return;
    setConfig({ ...config, custom_qa: config.custom_qa.filter((_, i) => i !== index) });
  };

  if (!config) return <div className="p-6 text-slate-500">Loading...</div>;

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div>
        <h2 className="text-lg font-bold text-slate-900">AI Chatbot</h2>
        <p className="text-sm text-slate-600 mt-1">
          Add an AI-powered chat assistant to your public website. Bring your own API key.
        </p>
      </div>

      {/* Enable toggle */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setConfig({ ...config, is_enabled: !config.is_enabled })}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            config.is_enabled ? 'bg-blue-600' : 'bg-slate-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              config.is_enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
        <span className="text-sm font-medium text-slate-700">
          {config.is_enabled ? 'Chatbot enabled' : 'Chatbot disabled'}
        </span>
      </div>

      {/* Provider */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">AI Provider</label>
        <select
          value={config.provider}
          onChange={(e) => {
            const p = e.target.value;
            setConfig({
              ...config,
              provider: p,
              model_name: PROVIDER_MODELS[p]?.[0]?.value || config.model_name,
            });
          }}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          {CHATBOT_PROVIDERS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      {/* Model */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">Model</label>
        <select
          value={config.model_name}
          onChange={(e) => setConfig({ ...config, model_name: e.target.value })}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          {(PROVIDER_MODELS[config.provider] || []).map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      {/* API Key */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">
          API Key{' '}
          {config.has_api_key && <span className="text-green-600 font-normal">(configured)</span>}
        </label>
        <PasswordInput
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={
            config.has_api_key ? 'Enter new key to replace existing' : 'Paste your API key here'
          }
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
          autoComplete="new-password"
        />
        <p className="mt-1 text-xs text-slate-500">
          Your key is encrypted at rest and never returned in API responses.
        </p>
      </div>

      {/* System Prompt */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">System Prompt</label>
        <textarea
          value={config.system_prompt}
          onChange={(e) => setConfig({ ...config, system_prompt: e.target.value })}
          rows={5}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm resize-none"
          placeholder="Describe your dealership, financing options, and how you want the assistant to respond..."
        />
      </div>

      {/* Custom Q&A */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-slate-700">Custom Q&amp;A</label>
          <button
            onClick={addQAPair}
            className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
          >
            + Add Pair
          </button>
        </div>
        <div className="space-y-3">
          {config.custom_qa.map((qa, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2">
              <input
                type="text"
                value={qa.question}
                onChange={(e) => updateQAPair(i, 'question', e.target.value)}
                placeholder="Question"
                className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm"
              />
              <input
                type="text"
                value={qa.answer}
                onChange={(e) => updateQAPair(i, 'answer', e.target.value)}
                placeholder="Answer"
                className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm"
              />
              <button
                onClick={() => removeQAPair(i)}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Remove
              </button>
            </div>
          ))}
          {config.custom_qa.length === 0 && (
            <p className="text-xs text-slate-500">
              No custom Q&amp;A yet. Add pairs to train the assistant on your dealership's specific
              answers.
            </p>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      <button
        onClick={save}
        disabled={saving}
        className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Chatbot Settings'}
      </button>
    </div>
  );
};

const SettingsPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>('dealership');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('general');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['dealership'])
  );
  const [searchParams] = useSearchParams();

  // Deep-link support: ?category=gps&sub=general opens that tab directly. Lets
  // actionable links (e.g. the "Set up GPS credentials" error toast) jump
  // straight to the right settings page.
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (!categoryParam || !Object.prototype.hasOwnProperty.call(settingsTree, categoryParam))
      return;
    setSelectedCategory(categoryParam);
    setExpandedCategories((prev) => new Set(prev).add(categoryParam));
    const category = settingsTree[categoryParam as keyof typeof settingsTree];
    if ('children' in category) {
      const subParam = searchParams.get('sub');
      const children = Object.keys(category.children);
      setSelectedSubcategory(subParam && children.includes(subParam) ? subParam : children[0]);
    }
  }, [searchParams]);

  const toggleCategory = (categoryKey: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryKey)) {
      newExpanded.delete(categoryKey);
      if (selectedCategory === categoryKey) {
        setSelectedCategory(null);
      }
    } else {
      newExpanded.add(categoryKey);
      setSelectedCategory(categoryKey);
      // Auto-select first subcategory
      const category = settingsTree[categoryKey as keyof typeof settingsTree];
      if ('children' in category) {
        const firstChild = Object.keys(category.children)[0];
        setSelectedSubcategory(firstChild);
      }
    }
    setExpandedCategories(newExpanded);
  };

  const selectSubcategory = (categoryKey: string, subcategoryKey: string) => {
    setSelectedCategory(categoryKey);
    setSelectedSubcategory(subcategoryKey);
  };

  const renderContent = () => {
    if (!selectedCategory) {
      return (
        <div className="flex items-center justify-center h-96 text-gray-500">
          <div className="text-center">
            <BellIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">Settings</h3>
            <p>Select a category from the menu to configure your dealership settings.</p>
          </div>
        </div>
      );
    }

    const category = settingsTree[selectedCategory as keyof typeof settingsTree];

    // Handle single-level categories (like All Notifications and AI Providers)
    if ('component' in category) {
      if (category.component === 'AllNotifications') {
        return <AllNotificationsComponent />;
      }
      if (category.component === 'AIProviders') {
        return <AIProviderSettingsPage />;
      }
      if (category.component === 'GPSProviders') {
        return <GPSProviderSettingsPage />;
      }
    }

    // Handle categories with children
    if ('children' in category && selectedSubcategory) {
      const subcategory = category.children[
        selectedSubcategory as keyof typeof category.children
      ] as any;

      switch (subcategory?.component) {
        case 'DealershipGeneral':
          return <DealershipGeneralComponent />;
        case 'DealershipWebsite':
          return <DealershipWebsiteComponent />;
        case 'DealershipChatbot':
          return <ChatbotSettingsPanel />;
        case 'InventoryGeneral':
          return (
            <InventoryGeneralComponent
              onNavigateToImport={() => selectSubcategory('inventory', 'import')}
            />
          );
        case 'InventoryWarranty':
          return <InventoryWarrantyComponent />;
        case 'InventoryImport':
          return <InventoryImportSettings />;
        case 'InventoryPartners':
          return <InventoryPartnersComponent />;
        case 'CustomerImport':
          return <CustomerImportComponent />;
        case 'CustomerLeadManagement':
          return <CustomerLeadManagementComponent />;
        case 'DealGeneral':
          return <DealGeneralComponent />;
        case 'BHPHGeneral':
          return <BHPHGeneralComponent />;
        default:
          return <PlaceholderComponent section={subcategory?.label || 'Settings'} />;
      }
    }

    return <PlaceholderComponent section="Settings" />;
  };

  const getBreadcrumb = () => {
    if (!selectedCategory) return null;

    const category = settingsTree[selectedCategory as keyof typeof settingsTree];
    const breadcrumbs = [category.label];

    if ('children' in category && selectedSubcategory) {
      const subcategory = category.children[
        selectedSubcategory as keyof typeof category.children
      ] as any;
      breadcrumbs.push(subcategory?.label || 'Settings');
    }

    return (
      <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            {index > 0 && <ChevronRightIcon className="h-4 w-4" />}
            <span className={index === breadcrumbs.length - 1 ? 'font-medium text-gray-900' : ''}>
              {crumb}
            </span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
                <p className="text-sm text-gray-600">UNITED AUTO BROKERS</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex space-x-8">
          {/* Left Sidebar - Settings Tree */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Search */}
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search settings"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Settings Tree Navigation */}
              <nav className="max-h-96 overflow-y-auto">
                {Object.entries(settingsTree).map(([categoryKey, category]) => {
                  const Icon = category.icon;
                  const isExpanded = expandedCategories.has(categoryKey);
                  const isSelected = selectedCategory === categoryKey;
                  const hasChildren = 'children' in category;

                  return (
                    <div key={categoryKey} className="border-b border-gray-100 last:border-b-0">
                      <button
                        onClick={() =>
                          hasChildren
                            ? toggleCategory(categoryKey)
                            : setSelectedCategory(categoryKey)
                        }
                        className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 ${
                          isSelected
                            ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                            : 'text-gray-700'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon className="h-5 w-5 flex-shrink-0" />
                          <span className="font-medium">{category.label}</span>
                        </div>
                        {hasChildren && (
                          <ChevronRightIcon
                            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          />
                        )}
                      </button>

                      {/* Subcategories */}
                      {hasChildren && isExpanded && 'children' in category && (
                        <div className="bg-gray-50">
                          {Object.entries(category.children).map(([subKey, subcategory]) => (
                            <button
                              key={subKey}
                              onClick={() => selectSubcategory(categoryKey, subKey)}
                              className={`w-full text-left px-4 py-2 pl-12 text-sm hover:bg-gray-100 focus:outline-none focus:bg-gray-100 ${
                                selectedCategory === categoryKey && selectedSubcategory === subKey
                                  ? 'bg-blue-100 text-blue-700 font-medium'
                                  : 'text-gray-600'
                              }`}
                            >
                              {subcategory.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 bg-white rounded-lg border border-gray-200 min-h-96">
            <div className="p-6">
              {getBreadcrumb()}
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const settingsPlaceholderContent: Record<
  string,
  {
    summary: string;
    readiness: string[];
    preview: string[];
    opportunity: string;
  }
> = {
  'Dealership Website': {
    summary:
      'Website builder and inventory sync will be available for beta launch. Use the checklist below to prepare assets and branding details.',
    readiness: [
      'Confirm dealership logo + brand colors',
      'Prepare hero copy and contact details',
      'Select a template style (Classic, Modern, Bold, Professional, Budget)',
    ],
    preview: [
      'Template gallery + live preview',
      'Auto-sync inventory feed',
      'SEO-friendly listing pages',
    ],
    opportunity: 'Wire to /website/templates and /website/settings APIs once enabled.',
  },
};

// Placeholder component for sections not yet implemented
const PlaceholderComponent: React.FC<{ section: string }> = ({ section }) => {
  const content = settingsPlaceholderContent[section] || {
    summary:
      'This settings area is planned. Use the readiness checklist to prepare for activation.',
    readiness: [
      'Confirm required credentials and access',
      'Identify data fields needed for configuration',
      'Define desired sync/update cadence',
    ],
    preview: ['Configuration panel', 'Validation and status checks', 'Audit and activity history'],
    opportunity: 'Connect to the corresponding settings endpoint when available.',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 text-gray-400">
            <WrenchScrewdriverIcon className="h-12 w-12" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{section} Settings (Preview)</h3>
            <p className="text-sm text-gray-600">{content.summary}</p>
          </div>
        </div>
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Coming Soon
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Readiness Checklist</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            {content.readiness.map((item) => (
              <li key={item}>• {item}</li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Planned Capabilities</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            {content.preview.map((item) => (
              <li key={item}>• {item}</li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Replace With Live Data</h4>
          <p className="text-sm text-gray-600">{content.opportunity}</p>
        </div>
      </div>
    </div>
  );
};

interface FacebookPageConnection {
  id: string;
  account_id: string;
  account_name: string;
  is_default: boolean;
  status: string;
  token_expires_at?: string | null;
}

interface FacebookConfigStatus {
  facebook_app_configured: boolean;
  facebook_redirect_configured: boolean;
  required_facebook_scopes: string;
}

interface FacebookIntegrationSettings {
  app_id?: string | null;
  redirect_uri?: string | null;
  app_secret_configured: boolean;
  app_secret_last4?: string | null;
}

interface GoogleConfigStatus {
  google_client_configured: boolean;
  google_redirect_configured: boolean;
}

interface GoogleIntegrationSettings {
  merchant_id?: string;
  refresh_token_configured: boolean;
  connected: boolean;
}

export const InventoryPartnersComponent: React.FC = () => {
  const [pages, setPages] = useState<FacebookPageConnection[]>([]);
  const [configStatus, setConfigStatus] = useState<FacebookConfigStatus | null>(null);
  const [facebookSettings, setFacebookSettings] = useState<FacebookIntegrationSettings | null>(
    null
  );
  const [facebookForm, setFacebookForm] = useState({ appId: '', appSecret: '', redirectUri: '' });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Google Vehicle Listings state
  const [googleConfigStatus, setGoogleConfigStatus] = useState<GoogleConfigStatus | null>(null);
  const [googleSettings, setGoogleSettings] = useState<GoogleIntegrationSettings | null>(null);
  const [googleForm, setGoogleForm] = useState({ merchantId: '' });
  const [googleSaving, setGoogleSaving] = useState(false);

  const fetchPages = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get<FacebookPageConnection[]>(
        '/marketplaces/facebook/pages'
      );
      setPages(response || []);
    } catch (err) {
      setError('Unable to load Facebook page connections.');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfigStatus = async () => {
    try {
      const response = await apiService.get<FacebookConfigStatus>('/marketplaces/facebook/config');
      setConfigStatus(response || null);
    } catch (err) {
      setConfigStatus(null);
    }
  };

  const fetchIntegrationSettings = async () => {
    try {
      const facebookResponse = await apiService.get<FacebookIntegrationSettings>(
        '/settings/integrations/facebook'
      );
      setFacebookSettings(facebookResponse || null);
      setFacebookForm({
        appId: facebookResponse?.app_id || '',
        appSecret: '',
        redirectUri: facebookResponse?.redirect_uri || '',
      });
    } catch (err) {
      setFacebookSettings(null);
    }
  };

  const fetchGoogleConfigStatus = async () => {
    try {
      const response = await apiService.get<GoogleConfigStatus>('/marketplaces/google/config');
      setGoogleConfigStatus(response || null);
    } catch {
      setGoogleConfigStatus(null);
    }
  };

  const fetchGoogleSettings = async () => {
    try {
      const response = await apiService.get<GoogleIntegrationSettings>(
        '/settings/integrations/google'
      );
      setGoogleSettings(response || null);
      setGoogleForm({ merchantId: response?.merchant_id || '' });
    } catch {
      setGoogleSettings(null);
    }
  };

  useEffect(() => {
    fetchPages();
    fetchConfigStatus();
    fetchIntegrationSettings();
    fetchGoogleConfigStatus();
    fetchGoogleSettings();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('facebook') === 'connected') {
      fetchPages();
      params.delete('facebook');
      const newQuery = params.toString();
      const newUrl = newQuery
        ? `${window.location.pathname}?${newQuery}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      fetchGoogleSettings();
      params.delete('google');
      const newQuery = params.toString();
      const newUrl = newQuery
        ? `${window.location.pathname}?${newQuery}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const handleConnect = async () => {
    try {
      const response = await apiService.post<{ auth_url: string }>(
        '/marketplaces/facebook/oauth/url',
        { redirect_url: window.location.href }
      );
      if (response?.auth_url) {
        window.location.href = response.auth_url;
      }
    } catch (err) {
      setError('Unable to start Facebook OAuth flow.');
    }
  };

  const handleSetDefault = async (pageId: string) => {
    try {
      await apiService.post('/marketplaces/facebook/pages/default', { page_id: pageId });
      await fetchPages();
    } catch (err) {
      setError('Unable to set default Facebook page.');
    }
  };

  const handleDisconnect = async (pageId: string) => {
    try {
      await apiService.post('/marketplaces/facebook/pages/disconnect', { page_id: pageId });
      await fetchPages();
    } catch (err) {
      setError('Unable to disconnect Facebook page.');
    }
  };

  const handleSaveFacebookSettings = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiService.post('/settings/integrations/facebook', {
        app_id: facebookForm.appId || null,
        app_secret: facebookForm.appSecret || null,
        redirect_uri: facebookForm.redirectUri || null,
      });
      await fetchIntegrationSettings();
      await fetchConfigStatus();
      setFacebookForm((prev) => ({ ...prev, appSecret: '' }));
    } catch (err) {
      setError('Unable to save Facebook app settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleGoogleConnect = async () => {
    try {
      const response = await apiService.post<{ auth_url: string }>(
        '/marketplaces/google/oauth/url',
        { redirect_url: window.location.href }
      );
      if (response?.auth_url) {
        window.location.href = response.auth_url;
      }
    } catch {
      setError('Unable to start Google OAuth flow.');
    }
  };

  const handleSaveGoogleSettings = async () => {
    setGoogleSaving(true);
    setError(null);
    try {
      await apiService.post('/settings/integrations/google', {
        merchant_id: googleForm.merchantId || null,
      });
      await fetchGoogleSettings();
      await fetchGoogleConfigStatus();
    } catch {
      setError('Unable to save Google settings.');
    } finally {
      setGoogleSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Marketplace Partners</h2>
        <p className="text-sm text-gray-600 mt-1">
          Connect your Facebook business pages to enable auto-posting with AI-generated copy.
        </p>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <h3 className="text-sm font-semibold">SaaS Setup (Bring Your Own Facebook App)</h3>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-blue-800">
          <li>Create a Facebook App in Meta for Developers.</li>
          <li>Add the Facebook Login product and set your OAuth redirect URL.</li>
          <li>Provide your App ID/Secret to SimpleDealer support for secure tenant setup.</li>
          <li>Connect your business page below to enable auto-posting.</li>
        </ol>
        <div className="mt-2 text-xs text-blue-700">
          Required scopes:{' '}
          {configStatus?.required_facebook_scopes ||
            'pages_show_list, pages_read_engagement, pages_manage_posts'}
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <span
            className={`rounded-full px-2 py-1 ${configStatus?.facebook_app_configured ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
          >
            Facebook App: {configStatus?.facebook_app_configured ? 'Configured' : 'Not configured'}
          </span>
          <span
            className={`rounded-full px-2 py-1 ${configStatus?.facebook_redirect_configured ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
          >
            Redirect URI:{' '}
            {configStatus?.facebook_redirect_configured ? 'Configured' : 'Not configured'}
          </span>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900">Facebook App Credentials</h3>
        <p className="text-xs text-gray-500 mt-1">
          Saved securely per tenant. We never display the full secret again.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700">App ID</label>
            <input
              type="text"
              value={facebookForm.appId}
              onChange={(event) =>
                setFacebookForm((prev) => ({ ...prev, appId: event.target.value }))
              }
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="1234567890"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700">App Secret</label>
            <PasswordInput
              value={facebookForm.appSecret}
              onChange={(event) =>
                setFacebookForm((prev) => ({ ...prev, appSecret: event.target.value }))
              }
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder={
                facebookSettings?.app_secret_configured
                  ? `••••${facebookSettings?.app_secret_last4 || ''}`
                  : 'Enter app secret'
              }
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700">OAuth Redirect URI</label>
            <input
              type="url"
              value={facebookForm.redirectUri}
              onChange={(event) =>
                setFacebookForm((prev) => ({ ...prev, redirectUri: event.target.value }))
              }
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="https://api.carlotpilot.com/api/v1/marketplaces/facebook/oauth/callback"
            />
          </div>
          <button className="btn-primary" onClick={handleSaveFacebookSettings} disabled={saving}>
            {saving ? 'Saving...' : 'Save Facebook Settings'}
          </button>
        </div>
        <p className="mt-4 text-xs text-slate-400">
          AI post copy is configured in Settings &rarr; AI Providers (Online Marketing task).
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          className="btn-primary"
          onClick={handleConnect}
          disabled={
            !configStatus?.facebook_app_configured || !configStatus?.facebook_redirect_configured
          }
        >
          Connect Facebook Pages
        </button>
        <span className="text-xs text-gray-500">
          Requires Facebook App ID/Secret configured on the server.
        </span>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">Connected Facebook Pages</h3>
        </div>
        <div className="p-4">
          {loading ? (
            <p className="text-sm text-gray-500">Loading connections...</p>
          ) : pages.length === 0 ? (
            <p className="text-sm text-gray-500">No Facebook pages connected yet.</p>
          ) : (
            <div className="space-y-3">
              {pages.map((page) => (
                <div
                  key={page.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{page.account_name}</p>
                    <p className="text-xs text-gray-500">Page ID: {page.account_id}</p>
                    <p className="text-xs text-gray-500">Status: {page.status}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {page.is_default ? (
                      <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                        Default
                      </span>
                    ) : (
                      <button
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => handleSetDefault(page.account_id)}
                      >
                        Set Default
                      </button>
                    )}
                    <button
                      className="text-xs text-red-600 hover:underline"
                      onClick={() => handleDisconnect(page.account_id)}
                    >
                      Disconnect
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Google Vehicle Listings ──────────────────────────────────────── */}
      <div className="rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Google Vehicle Listings</h3>
            <p className="text-xs text-gray-500 mt-1">
              Connect to Google's Vehicle Listings feed to surface your inventory in Google Search
              and Maps. OAuth is managed by the platform — enter your Merchant Center ID below.
            </p>
          </div>
          <span
            data-testid="google-status"
            className={`text-xs font-medium px-2 py-1 rounded-full ${
              googleSettings?.connected
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {googleSettings?.connected ? 'Connected' : 'Not connected'}
          </span>
        </div>

        {/* Platform OAuth readiness badges */}
        <div className="flex flex-wrap gap-2 text-xs mb-4">
          <span
            className={`rounded-full px-2 py-1 ${googleConfigStatus?.google_client_configured ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
          >
            OAuth Client:{' '}
            {googleConfigStatus?.google_client_configured ? 'Configured' : 'Not configured'}
          </span>
          <span
            className={`rounded-full px-2 py-1 ${googleConfigStatus?.google_redirect_configured ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
          >
            Redirect URI:{' '}
            {googleConfigStatus?.google_redirect_configured ? 'Configured' : 'Not configured'}
          </span>
        </div>

        {/* Merchant ID field — only tenant-supplied field; no client_id/secret/redirect */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">Merchant Center ID</label>
          <input
            type="text"
            value={googleForm.merchantId}
            onChange={(e) => setGoogleForm((prev) => ({ ...prev, merchantId: e.target.value }))}
            placeholder="Merchant ID"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            className="btn-primary"
            onClick={handleGoogleConnect}
            disabled={
              !googleConfigStatus?.google_client_configured ||
              !googleConfigStatus?.google_redirect_configured
            }
          >
            Connect Google
          </button>
          <button
            className="btn-primary"
            onClick={handleSaveGoogleSettings}
            disabled={googleSaving}
          >
            {googleSaving ? 'Saving...' : 'Save Google Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface DealershipGeneralFormState {
  company_name: string;
  company_name_alias: string;
  dealer_license_no: string;
  dealer_no: string;
  phone_no: string;
  whatsapp_no: string;
  text_no: string;
  fax_no: string;
  email: string;
  website: string;
  physical_address: string;
  physical_city: string;
  physical_state: string;
  physical_zip: string;
}

const defaultDealershipGeneralFormState: DealershipGeneralFormState = {
  company_name: '',
  company_name_alias: '',
  dealer_license_no: '',
  dealer_no: '',
  phone_no: '',
  whatsapp_no: '',
  text_no: '',
  fax_no: '',
  email: '',
  website: '',
  physical_address: '',
  physical_city: '',
  physical_state: '',
  physical_zip: '',
};

// Main settings components
const DealershipGeneralComponent: React.FC = () => {
  const [form, setForm] = useState<DealershipGeneralFormState>(defaultDealershipGeneralFormState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const response = await apiService.get<Partial<DealershipGeneralFormState> | null>(
          '/settings/dealership'
        );
        if (response) {
          const hydrated = Object.keys(defaultDealershipGeneralFormState).reduce(
            (acc, key) => {
              const typedKey = key as keyof DealershipGeneralFormState;
              acc[typedKey] = String(response[typedKey] ?? '');
              return acc;
            },
            { ...defaultDealershipGeneralFormState }
          );

          setForm((prev) => ({
            ...prev,
            ...hydrated,
          }));
        }
      } catch (error) {
        console.error('Failed to load dealership settings:', error);
        setStatusMessage('Unable to load dealership settings.');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleChange = (field: keyof DealershipGeneralFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setStatusMessage(null);

      await apiService.post('/settings/dealership', {
        ...form,
        company_name: form.company_name || 'My Dealership',
      });

      setStatusMessage('Dealership settings saved successfully.');
    } catch (error) {
      console.error('Failed to save dealership settings:', error);
      setStatusMessage('Failed to save dealership settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Dealer Setting</h2>
      <p className="text-gray-600 mb-6">
        Setting for basic dealership information like tax/EIN numbers, contact info, and the
        dealership's address.
      </p>

      {loading ? (
        <div className="text-sm text-gray-500">Loading dealership settings...</div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <input
                type="text"
                value={form.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name Alias
              </label>
              <input
                type="text"
                value={form.company_name_alias}
                onChange={(e) => handleChange('company_name_alias', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dealer License No.
              </label>
              <input
                type="text"
                value={form.dealer_license_no}
                onChange={(e) => handleChange('dealer_license_no', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dealer No.</label>
              <input
                type="text"
                value={form.dealer_no}
                onChange={(e) => handleChange('dealer_no', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dealer Phone Number
              </label>
              <PhoneInput
                value={form.phone_no}
                onChange={(v) => handleChange('phone_no', v)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="(555) 555-0199"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                WhatsApp Number
              </label>
              <PhoneInput
                value={form.whatsapp_no}
                onChange={(v) => handleChange('whatsapp_no', v)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text / SMS Number
              </label>
              <PhoneInput
                value={form.text_no}
                onChange={(v) => handleChange('text_no', v)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fax Number</label>
              <PhoneInput
                value={form.fax_no}
                onChange={(v) => handleChange('fax_no', v)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dealer Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
              <input
                id="website-builder-headline"
                value={form.website}
                onChange={(e) => handleChange('website', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Physical Address
              </label>
              <input
                type="text"
                value={form.physical_address}
                onChange={(e) => handleChange('physical_address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
              <input
                type="text"
                value={form.physical_city}
                onChange={(e) => handleChange('physical_city', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
              <input
                type="text"
                value={form.physical_state}
                onChange={(e) => handleChange('physical_state', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ZIP</label>
              <input
                type="text"
                value={form.physical_zip}
                onChange={(e) => handleChange('physical_zip', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save General Settings'}
            </button>
            {statusMessage && <span className="text-sm text-gray-600">{statusMessage}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

interface WebsiteTemplateOption {
  id: string;
  name: string;
  description: string;
  tags: string[];
  accent: string;
}

interface WebsiteBuilderSettings {
  templateId: string;
  branding: {
    logoUrl: string;
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    fontFamily: string;
  };
  hero: {
    headline: string;
    subheadline: string;
    ctaLabel: string;
  };
  contact: {
    phone: string;
    email: string;
    address: string;
  };
  domain: {
    subdomain: string;
    customDomain: string;
  };
  features: {
    inventorySync: boolean;
    financingCta: boolean;
    tradeInCta: boolean;
    leadCapture: boolean;
  };
  analytics: {
    gaMeasurementId: string;
  };
}

interface WebsitePreviewVehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  asking_price?: number | null;
  status?: string;
}

interface WebsiteSyncStatus {
  inventorySyncEnabled: boolean;
  totalVehicles: number;
  lastSyncAt?: string | null;
  lastVehicleUpdateAt?: string | null;
}

const websiteTemplateOptions: WebsiteTemplateOption[] = [
  {
    id: 'classic',
    name: 'Classic Dealer',
    description: 'Traditional dealership layout with inventory-first sections and bold CTAs.',
    tags: ['Classic', 'Inventory-first', 'Bold CTA'],
    accent: 'from-slate-800 via-blue-700 to-blue-500',
  },
  {
    id: 'modern',
    name: 'Modern Minimal',
    description: 'Clean typography, airy spacing, and premium showcase blocks.',
    tags: ['Minimal', 'Premium', 'High-end'],
    accent: 'from-gray-900 via-gray-700 to-gray-500',
  },
  {
    id: 'bold',
    name: 'Bold & Colorful',
    description: 'Vibrant palette, dynamic hero layouts, and expressive feature tiles.',
    tags: ['Vibrant', 'Youthful', 'Promos'],
    accent: 'from-fuchsia-600 via-rose-500 to-orange-400',
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'Corporate tone with trust builders, finance highlights, and service panels.',
    tags: ['Corporate', 'Trust', 'Finance'],
    accent: 'from-emerald-700 via-teal-600 to-cyan-500',
  },
  {
    id: 'budget',
    name: 'Budget Friendly',
    description: 'High-volume inventory grid with payment callouts and quick filters.',
    tags: ['Value', 'Payments', 'High-volume'],
    accent: 'from-amber-600 via-yellow-500 to-lime-400',
  },
  {
    id: 'saas_bilingual',
    name: 'SaaS Bilingual Inventory',
    description:
      'Modern bilingual (EN/ES) inventory-first website with integrated public lead capture.',
    tags: ['Bilingual', 'SaaS', 'Lead Capture'],
    accent: 'from-blue-700 via-cyan-600 to-teal-500',
  },
];

const defaultWebsiteBuilderSettings: WebsiteBuilderSettings = {
  templateId: 'classic',
  branding: {
    logoUrl: '',
    primaryColor: '#1d4ed8',
    secondaryColor: '#0f172a',
    accentColor: '#22c55e',
    fontFamily: 'Inter',
  },
  hero: {
    headline: 'Find your next ride today',
    subheadline: 'Quality inventory, flexible financing, and fast approvals with a team you trust.',
    ctaLabel: 'Browse Inventory',
  },
  contact: {
    phone: '(555) 555-0199',
    email: 'sales@mydealership.com',
    address: '123 Main St, Orlando, FL',
  },
  domain: {
    subdomain: 'mydealership',
    customDomain: '',
  },
  features: {
    inventorySync: true,
    financingCta: true,
    tradeInCta: true,
    leadCapture: true,
  },
  analytics: {
    gaMeasurementId: '',
  },
};

const DealershipWebsiteComponent: React.FC = () => {
  const [settings, setSettings] = useState<WebsiteBuilderSettings>(defaultWebsiteBuilderSettings);
  const [templateOptions, setTemplateOptions] =
    useState<WebsiteTemplateOption[]>(websiteTemplateOptions);
  const [previewVehicles, setPreviewVehicles] = useState<WebsitePreviewVehicle[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<WebsiteSyncStatus | null>(null);
  const [syncStatusLoading, setSyncStatusLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    const loadWebsiteBuilderSettings = async () => {
      try {
        const templates = (await apiService.get('/website/templates')) as WebsiteTemplateOption[];
        if (templates && templates.length > 0) {
          setTemplateOptions(templates);
        }
      } catch (error) {
        console.warn('⚠️ Failed to load website templates from API. Using local defaults.', error);
      }

      try {
        const serverSettings = (await apiService.get(
          '/website/settings'
        )) as WebsiteBuilderSettings;
        if (serverSettings) {
          setSettings({
            ...defaultWebsiteBuilderSettings,
            ...serverSettings,
            branding: { ...defaultWebsiteBuilderSettings.branding, ...serverSettings.branding },
            hero: { ...defaultWebsiteBuilderSettings.hero, ...serverSettings.hero },
            contact: { ...defaultWebsiteBuilderSettings.contact, ...serverSettings.contact },
            domain: { ...defaultWebsiteBuilderSettings.domain, ...serverSettings.domain },
            features: { ...defaultWebsiteBuilderSettings.features, ...serverSettings.features },
            analytics: { ...defaultWebsiteBuilderSettings.analytics, ...serverSettings.analytics },
          });
          safeStorage.setItem('websiteBuilderSettings', JSON.stringify(serverSettings));
          return;
        }
      } catch (error) {
        console.warn(
          '⚠️ Failed to load website settings from API. Falling back to local storage.',
          error
        );
      }

      const saved = safeStorage.getItem('websiteBuilderSettings');
      if (!saved) return;
      try {
        const localSettings = JSON.parse(saved) as WebsiteBuilderSettings;
        setSettings({
          ...defaultWebsiteBuilderSettings,
          ...localSettings,
          branding: { ...defaultWebsiteBuilderSettings.branding, ...localSettings.branding },
          hero: { ...defaultWebsiteBuilderSettings.hero, ...localSettings.hero },
          contact: { ...defaultWebsiteBuilderSettings.contact, ...localSettings.contact },
          domain: { ...defaultWebsiteBuilderSettings.domain, ...localSettings.domain },
          features: { ...defaultWebsiteBuilderSettings.features, ...localSettings.features },
          analytics: { ...defaultWebsiteBuilderSettings.analytics, ...localSettings.analytics },
        });
      } catch (error) {
        console.error('Error loading website builder settings:', error);
      }
    };

    loadWebsiteBuilderSettings();
  }, []);

  useEffect(() => {
    const loadPreviewVehicles = async () => {
      if (!settings.features.inventorySync) {
        setPreviewVehicles([]);
        setPreviewError(null);
        setPreviewLoading(false);
        return;
      }

      try {
        setPreviewLoading(true);
        setPreviewError(null);
        const vehicles = await apiService.get<WebsitePreviewVehicle[]>('/vehicles/?limit=4');
        setPreviewVehicles(Array.isArray(vehicles) ? vehicles : []);
      } catch (error) {
        console.error('Error loading preview vehicles:', error);
        setPreviewError('Unable to load live inventory preview.');
      } finally {
        setPreviewLoading(false);
      }
    };

    loadPreviewVehicles();
  }, [settings.features.inventorySync]);

  useEffect(() => {
    const loadSyncStatus = async () => {
      try {
        setSyncStatusLoading(true);
        const status = await apiService.get<WebsiteSyncStatus>('/website/status');
        setSyncStatus(status ?? null);
      } catch (error) {
        console.error('Error loading website sync status:', error);
        setSyncStatus(null);
      } finally {
        setSyncStatusLoading(false);
      }
    };

    loadSyncStatus();
  }, [settings.features.inventorySync]);

  const selectedTemplate =
    templateOptions.find((template) => template.id === settings.templateId) ??
    templateOptions[0] ??
    websiteTemplateOptions[0];

  const formatPrice = (value?: number | null) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return 'Contact for price';
    }
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  };

  const formatTimestamp = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
  const domainPattern = /^([a-z0-9-]+\.)+[a-z]{2,}$/i;
  const gaMeasurementIdPattern = /^G-[A-Z0-9]{4,20}$/;

  const validationIssues = [
    !settings.hero.headline.trim() ? 'Add a hero headline' : null,
    !settings.contact.phone.trim() ? 'Add a contact phone number' : null,
    !emailPattern.test(settings.contact.email.trim()) ? 'Add a valid contact email' : null,
    !settings.domain.customDomain.trim() ? 'Add a custom domain' : null,
    settings.domain.customDomain.trim() && !domainPattern.test(settings.domain.customDomain.trim())
      ? 'Custom domain format looks invalid'
      : null,
    settings.analytics.gaMeasurementId.trim() &&
    !gaMeasurementIdPattern.test(settings.analytics.gaMeasurementId.trim().toUpperCase())
      ? 'Google Analytics ID must look like G-XXXXXXXXXX (GA4 format)'
      : null,
  ].filter((issue): issue is string => Boolean(issue));

  const canPublish = validationIssues.length === 0;

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      await apiService.post('/website/settings', settings);
      safeStorage.setItem('websiteBuilderSettings', JSON.stringify(settings));
      setSaveMessage('✅ Website settings saved successfully!');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving website settings:', error);
      setSaveMessage('❌ Failed to save website settings.');
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(defaultWebsiteBuilderSettings);
    safeStorage.setItem('websiteBuilderSettings', JSON.stringify(defaultWebsiteBuilderSettings));
    setSaveMessage('🔄 Website builder defaults restored.');
    setTimeout(() => setSaveMessage(null), 3000);
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Website Builder</h2>
        <p className="text-gray-600">
          Select a template, customize branding, and configure your inventory sync.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Template Gallery</h3>
            <p className="text-sm text-gray-600">Choose one of five production-ready layouts.</p>
          </div>
          <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
            Preview mode
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {templateOptions.map((template) => (
            <button
              key={template.id}
              type="button"
              onClick={() => setSettings((prev) => ({ ...prev, templateId: template.id }))}
              className={`text-left border rounded-lg p-4 transition-all hover:border-blue-400 hover:shadow-sm ${
                settings.templateId === template.id
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-gray-200'
              }`}
            >
              <div className={`h-24 rounded-md bg-gradient-to-br ${template.accent} mb-3`} />
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">{template.name}</h4>
                  <p className="text-xs text-gray-600 mt-1">{template.description}</p>
                </div>
                {settings.templateId === template.id && (
                  <span className="text-[10px] uppercase tracking-wide text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                    Selected
                  </span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {template.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Branding & Identity</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Logo URL</label>
                <input
                  type="text"
                  value={settings.branding.logoUrl}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      branding: { ...prev.branding, logoUrl: e.target.value },
                    }))
                  }
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
                <input
                  type="text"
                  value={settings.branding.fontFamily}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      branding: { ...prev.branding, fontFamily: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={settings.branding.primaryColor}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        branding: { ...prev.branding, primaryColor: e.target.value },
                      }))
                    }
                    className="h-10 w-12 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value={settings.branding.primaryColor}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        branding: { ...prev.branding, primaryColor: e.target.value },
                      }))
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secondary Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={settings.branding.secondaryColor}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        branding: { ...prev.branding, secondaryColor: e.target.value },
                      }))
                    }
                    className="h-10 w-12 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value={settings.branding.secondaryColor}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        branding: { ...prev.branding, secondaryColor: e.target.value },
                      }))
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={settings.branding.accentColor}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        branding: { ...prev.branding, accentColor: e.target.value },
                      }))
                    }
                    className="h-10 w-12 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value={settings.branding.accentColor}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        branding: { ...prev.branding, accentColor: e.target.value },
                      }))
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Hero Content</h3>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="website-builder-headline"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Headline
                </label>
                <input
                  id="website-builder-headline"
                  type="text"
                  value={settings.hero.headline}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      hero: { ...prev.hero, headline: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subheadline</label>
                <textarea
                  rows={3}
                  value={settings.hero.subheadline}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      hero: { ...prev.hero, subheadline: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary CTA Label
                </label>
                <input
                  type="text"
                  value={settings.hero.ctaLabel}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      hero: { ...prev.hero, ctaLabel: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Contact & Domain</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <PhoneInput
                  value={settings.contact.phone}
                  onChange={(v) =>
                    setSettings((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, phone: v },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={settings.contact.email}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, email: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <input
                  type="text"
                  value={settings.contact.address}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, address: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label
                  htmlFor="website-builder-custom-domain"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Custom Domain
                </label>
                <input
                  id="website-builder-custom-domain"
                  type="text"
                  value={settings.domain.customDomain}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      domain: { ...prev.domain, customDomain: e.target.value },
                    }))
                  }
                  placeholder="www.yourdomain.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                {settings.domain.customDomain.trim() && (
                  <p className="mt-2 text-xs text-amber-600">
                    Custom domains require DNS verification before publishing.
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="website-builder-ga-id"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Google Analytics Measurement ID
                </label>
                <input
                  id="website-builder-ga-id"
                  type="text"
                  value={settings.analytics.gaMeasurementId}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      analytics: { ...prev.analytics, gaMeasurementId: e.target.value },
                    }))
                  }
                  placeholder="G-XXXXXXXXXX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Paste the Measurement ID from your GA4 property (Admin → Data Streams → your web
                  stream). Leave blank to disable analytics on the public site.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Inventory & Lead Capture</h3>
            <div className="space-y-3">
              {(
                [
                  {
                    key: 'inventorySync',
                    label: 'Auto-sync inventory feed',
                    helper: 'Publish active inventory to the website in real time.',
                  },
                  {
                    key: 'financingCta',
                    label: 'Show financing CTA',
                    helper: 'Highlight “Get Approved” actions across the site.',
                  },
                  {
                    key: 'tradeInCta',
                    label: 'Enable trade-in CTA',
                    helper: 'Offer trade-in appraisal prompts.',
                  },
                  {
                    key: 'leadCapture',
                    label: 'Enable lead capture forms',
                    helper: 'Use embedded forms for inquiries and test drives.',
                  },
                ] as Array<{
                  key: keyof WebsiteBuilderSettings['features'];
                  label: string;
                  helper: string;
                }>
              ).map((feature) => (
                <button
                  key={feature.key}
                  type="button"
                  onClick={() =>
                    setSettings((prev) => ({
                      ...prev,
                      features: { ...prev.features, [feature.key]: !prev.features[feature.key] },
                    }))
                  }
                  className="w-full flex items-center justify-between border border-gray-200 rounded-md px-4 py-3 text-left hover:border-blue-400"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{feature.label}</p>
                    <p className="text-xs text-gray-500">{feature.helper}</p>
                  </div>
                  <span
                    className={`inline-flex h-6 w-11 items-center rounded-full ${
                      settings.features[feature.key] ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        settings.features[feature.key] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div
            className={`border rounded-lg p-4 ${canPublish ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">Publish Readiness</h3>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${canPublish ? 'bg-emerald-200 text-emerald-800' : 'bg-amber-200 text-amber-800'}`}
              >
                {canPublish ? 'Ready' : 'Needs attention'}
              </span>
            </div>
            {canPublish ? (
              <p className="text-xs text-emerald-800">
                All required fields are complete. You can publish when the site runtime is
                available.
              </p>
            ) : (
              <div className="text-xs text-amber-800 space-y-1">
                <p>Complete the items below before publishing:</p>
                <ul className="list-disc ml-4 space-y-1">
                  {validationIssues.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Live Preview</h3>
            <div
              className="rounded-lg border border-gray-200 overflow-hidden"
              style={{ fontFamily: settings.branding.fontFamily }}
            >
              <div
                className="p-4"
                style={{ backgroundColor: settings.branding.secondaryColor, color: '#fff' }}
              >
                <div className="text-xs uppercase tracking-wide text-white/70">
                  {selectedTemplate.name}
                </div>
                <div className="text-lg font-semibold">{settings.hero.headline}</div>
                <p className="text-xs text-white/80 mt-1">{settings.hero.subheadline}</p>
                <button
                  type="button"
                  className="mt-3 px-3 py-1.5 text-xs font-semibold rounded"
                  style={{
                    backgroundColor: settings.branding.accentColor,
                    color: settings.branding.secondaryColor,
                  }}
                >
                  {settings.hero.ctaLabel}
                </button>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>
                    {settings.features.inventorySync
                      ? 'Inventory cards (live)'
                      : 'Inventory sync paused'}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${settings.features.inventorySync ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
                  >
                    {settings.features.inventorySync ? 'Sync enabled' : 'Sync off'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 text-[10px] text-gray-500">
                  <span>
                    Vehicles: {syncStatusLoading ? 'Loading…' : (syncStatus?.totalVehicles ?? '—')}
                  </span>
                  <span>
                    Last sync:{' '}
                    {syncStatusLoading ? 'Loading…' : formatTimestamp(syncStatus?.lastSyncAt)}
                  </span>
                  <span>
                    Last update:{' '}
                    {syncStatusLoading
                      ? 'Loading…'
                      : formatTimestamp(syncStatus?.lastVehicleUpdateAt)}
                  </span>
                </div>
                {previewLoading && settings.features.inventorySync && (
                  <div className="grid grid-cols-2 gap-2">
                    {[1, 2, 3, 4].map((item) => (
                      <div
                        key={item}
                        className="border border-gray-200 rounded-md p-2 animate-pulse"
                      >
                        <div className="h-12 rounded bg-gray-100" />
                        <div className="h-3 bg-gray-100 rounded mt-2" />
                        <div className="h-3 bg-gray-100 rounded mt-1 w-3/4" />
                      </div>
                    ))}
                  </div>
                )}
                {!previewLoading && previewError && (
                  <div className="text-xs text-rose-600">{previewError}</div>
                )}
                {!previewLoading && !previewError && (
                  <div className="grid grid-cols-2 gap-2">
                    {(settings.features.inventorySync ? previewVehicles : []).length === 0 ? (
                      <div className="col-span-2 text-xs text-gray-500">
                        No inventory found yet. Add vehicles to populate the website.
                      </div>
                    ) : (
                      previewVehicles.map((vehicle) => (
                        <div key={vehicle.id} className="border border-gray-200 rounded-md p-2">
                          <div className="h-12 rounded bg-gray-100" />
                          <div className="text-xs font-medium text-gray-700 mt-2">
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </div>
                          <div className="text-[10px] text-gray-500">
                            {formatPrice(vehicle.asking_price)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500">
              {settings.features.inventorySync
                ? 'Live inventory sync updates populate these tiles automatically.'
                : 'Enable inventory sync to populate live vehicle tiles.'}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Publish Checklist</h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Confirm logo and brand colors</li>
              <li>• Verify contact details and hours</li>
              <li>• Enable inventory sync for live listings</li>
              <li>• Connect a custom domain (optional)</li>
            </ul>
          </div>

          <div className="flex flex-col gap-3">
            {saveMessage && (
              <div className="text-sm text-gray-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                {saveMessage}
              </div>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-60"
            >
              {isSaving ? 'Saving...' : 'Save Website Settings'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Reset to Defaults
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface InventoryGeneralComponentProps {
  onNavigateToImport?: () => void;
}

interface InventoryGeneralSettings {
  source_type: 'in_house' | 'third_party';
  primary_source: string | null;
  warranty_options?: Array<{ label: string; content: string }>;
}

const InventoryGeneralComponent: React.FC<InventoryGeneralComponentProps> = ({
  onNavigateToImport,
}) => {
  const [inventorySourceType, setInventorySourceType] = useState<'in_house' | 'third_party'>(
    'in_house'
  );
  const [primaryImportSource, setPrimaryImportSource] = useState<string>('carsforsale');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Fetch settings from backend on mount
  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const response = await apiService.get<InventoryGeneralSettings>('/settings/inventory');
        if (response) {
          setInventorySourceType(response.source_type || 'in_house');
          setPrimaryImportSource(response.primary_source || 'carsforsale');
        }
      } catch (error) {
        console.error('Error fetching inventory settings:', error);
        // Fall back to localStorage defensively
        const saved = localStorage.getItem('inventorySettings');
        if (saved) {
          try {
            const settings = JSON.parse(saved);
            setInventorySourceType(settings.sourceType || 'in_house');
            setPrimaryImportSource(settings.primarySource || 'carsforsale');
          } catch {
            // Use defaults if localStorage parse fails
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Save settings via backend API
  const handleSaveSettings = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      const payload: InventoryGeneralSettings = {
        source_type: inventorySourceType,
        primary_source: inventorySourceType === 'third_party' ? primaryImportSource : null,
      };

      await apiService.post('/settings/inventory', payload);

      // Update localStorage as defensive backup
      localStorage.setItem(
        'inventorySettings',
        JSON.stringify({
          sourceType: inventorySourceType,
          primarySource: payload.primary_source,
        })
      );

      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage('Failed to save settings. Please try again.');
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading state during initial fetch
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading inventory settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">General</h2>
        <p className="text-gray-600 mb-6">
          Settings to control stock number auto generation, default status to use for new inventory,
          global search, custom status and alerts.
        </p>
      </div>

      {/* Inventory Source Type Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Inventory Source Type</h3>
        <p className="text-sm text-gray-600 mb-6">
          Choose how your dealership manages inventory. This affects available features and import
          options.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* In-House Option */}
          <button
            onClick={() => setInventorySourceType('in_house')}
            className={`relative p-6 border-2 rounded-xl text-left transition-all ${
              inventorySourceType === 'in_house'
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            {inventorySourceType === 'in_house' && (
              <div className="absolute top-3 right-3">
                <svg className="h-6 w-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
            <div className="flex items-center space-x-3 mb-3">
              <div
                className={`p-2 rounded-lg ${inventorySourceType === 'in_house' ? 'bg-blue-100' : 'bg-gray-100'}`}
              >
                <svg
                  className={`h-6 w-6 ${inventorySourceType === 'in_house' ? 'text-blue-600' : 'text-gray-500'}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
              </div>
              <h4
                className={`text-lg font-semibold ${inventorySourceType === 'in_house' ? 'text-blue-900' : 'text-gray-900'}`}
              >
                In-House Inventory
              </h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Manage your inventory directly. Add vehicles manually or use our mobile app for VIN
              scanning and photo uploads.
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li className="flex items-center">
                <svg
                  className="h-4 w-4 mr-2 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Manual vehicle entry
              </li>
              <li className="flex items-center">
                <svg
                  className="h-4 w-4 mr-2 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Mobile VIN scanner
              </li>
              <li className="flex items-center">
                <svg
                  className="h-4 w-4 mr-2 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Direct photo uploads
              </li>
            </ul>
          </button>

          {/* Third-Party Option */}
          <button
            onClick={() => setInventorySourceType('third_party')}
            className={`relative p-6 border-2 rounded-xl text-left transition-all ${
              inventorySourceType === 'third_party'
                ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            {inventorySourceType === 'third_party' && (
              <div className="absolute top-3 right-3">
                <svg className="h-6 w-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
            <div className="flex items-center space-x-3 mb-3">
              <div
                className={`p-2 rounded-lg ${inventorySourceType === 'third_party' ? 'bg-purple-100' : 'bg-gray-100'}`}
              >
                <svg
                  className={`h-6 w-6 ${inventorySourceType === 'third_party' ? 'text-purple-600' : 'text-gray-500'}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                  />
                </svg>
              </div>
              <h4
                className={`text-lg font-semibold ${inventorySourceType === 'third_party' ? 'text-purple-900' : 'text-gray-900'}`}
              >
                Third-Party Import
              </h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Import inventory from external listing services like CarsForSale.com or CarGurus via
              automated feeds.
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li className="flex items-center">
                <svg
                  className="h-4 w-4 mr-2 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Automated FTP/API sync
              </li>
              <li className="flex items-center">
                <svg
                  className="h-4 w-4 mr-2 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                CarsForSale.com integration
              </li>
              <li className="flex items-center">
                <svg
                  className="h-4 w-4 mr-2 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                CarGurus integration
              </li>
            </ul>
          </button>
        </div>
      </div>

      {/* Conditional: In-House Settings */}
      {inventorySourceType === 'in_house' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg
                className="h-6 w-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-medium text-blue-900 mb-2">Mobile Inventory App</h4>
              <p className="text-sm text-blue-700 mb-4">
                Use our mobile-optimized web pages to add inventory from your phone. Scan VIN
                barcodes and capture photos directly on the lot.
              </p>
              <div className="flex items-center space-x-4">
                <a
                  href="/vehicles"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Add Vehicle
                </a>
                <span className="text-sm text-blue-600">
                  or scan QR code from vehicle details page
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conditional: Third-Party Settings */}
      {inventorySourceType === 'third_party' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <h4 className="text-lg font-medium text-purple-900 mb-4">Primary Import Source</h4>
          <p className="text-sm text-purple-700 mb-4">
            Select your primary listing service. You can configure additional sources in Settings
            &gt; Inventory &gt; Import.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* CarsForSale */}
            <button
              onClick={() => setPrimaryImportSource('carsforsale')}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                primaryImportSource === 'carsforsale'
                  ? 'border-purple-500 bg-white ring-2 ring-purple-200'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">CarsForSale.com</span>
                {primaryImportSource === 'carsforsale' && (
                  <svg className="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <p className="text-xs text-gray-500">CSV/FTP feed integration</p>
              <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                Active
              </span>
            </button>

            {/* CarGurus */}
            <button
              onClick={() => setPrimaryImportSource('cargurus')}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                primaryImportSource === 'cargurus'
                  ? 'border-purple-500 bg-white ring-2 ring-purple-200'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">CarGurus</span>
                {primaryImportSource === 'cargurus' && (
                  <svg className="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <p className="text-xs text-gray-500">API/Feed integration</p>
              <span className="inline-block mt-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                Preview
              </span>
            </button>

            {/* AutoTrader */}
            <button
              disabled
              className="p-4 border-2 border-gray-200 rounded-lg text-left opacity-50 cursor-not-allowed"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-500">AutoTrader</span>
              </div>
              <p className="text-xs text-gray-400">API integration</p>
              <span className="inline-block mt-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                Planned
              </span>
            </button>
          </div>

          <div className="text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-lg p-3">
            Preview sources show planned integrations. Configure live connections in Settings &gt;
            Inventory &gt; Import once enabled.
          </div>

          <button
            onClick={onNavigateToImport}
            className="inline-flex items-center text-sm text-purple-600 hover:text-purple-800 font-medium"
          >
            Configure Import Settings
            <svg className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Default Vehicle Settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Default Vehicle Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Status for New Vehicle
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option>In Inventory</option>
              <option>Sold</option>
              <option>Hold</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Vehicle Sale Type
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option>Retail</option>
              <option>Wholesale</option>
            </select>
          </div>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div
          className={`text-center p-3 rounded-lg font-medium ${
            saveMessage.includes('successfully')
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {saveMessage}
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveSettings}
          disabled={isSaving}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Saving...
            </>
          ) : (
            'Save Settings'
          )}
        </button>
      </div>
    </div>
  );
};

const InventoryWarrantyComponent: React.FC = () => {
  interface WarrantyOption {
    label: string;
    content: string;
  }

  const [options, setOptions] = useState<WarrantyOption[]>([]);
  const [saving, setSaving] = useState(false);

  // Existing inventory settings (preserved on save)
  const [sourceType, setSourceType] = useState<'in_house' | 'third_party'>('in_house');
  const [primarySource, setPrimarySource] = useState<string | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await apiService.get<InventoryGeneralSettings>('/settings/inventory');
        if (response) {
          setSourceType(response.source_type || 'in_house');
          setPrimarySource(response.primary_source || null);
          setOptions(response.warranty_options ?? []);
        }
      } catch (error) {
        console.error('Error fetching warranty settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleAddOption = () => {
    setOptions((prev) => [...prev, { label: '', content: '' }]);
  };

  const handleChangeLabel = (index: number, value: string) => {
    setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, label: value } : o)));
  };

  const handleChangeContent = (index: number, value: string) => {
    setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, content: value } : o)));
  };

  const handleRemoveOption = (index: number) => {
    setOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const filtered = options.filter((o) => !(o.label.trim() === '' && o.content.trim() === ''));
      const payload: InventoryGeneralSettings = {
        source_type: sourceType,
        primary_source: primarySource,
        warranty_options: filtered,
      };
      await apiService.post('/settings/inventory', payload);
      setOptions(filtered);
      toast.success('Warranty options saved successfully!');
    } catch (error) {
      console.error('Error saving warranty settings:', error);
      toast.error('Failed to save warranty settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Warranty Disclaimer Options</h2>
        <p className="text-gray-600 mb-6">
          Define warranty disclaimer options for vehicles. Each option has a label shown in the
          dropdown and disclaimer text inserted into vehicle descriptions.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {/* Header row */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Warranty Disclaimer Options</h3>
          <button
            type="button"
            onClick={handleAddOption}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Option
          </button>
        </div>

        {/* Options list */}
        {options.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">
            No warranty options yet. Click 'Add Option' to create one.
          </p>
        ) : (
          <div className="space-y-3">
            {options.map((option, index) => (
              <div key={index} className="flex items-start gap-3">
                <input
                  type="text"
                  value={option.label}
                  onChange={(e) => handleChangeLabel(index, e.target.value)}
                  placeholder="Label, e.g. 30 Days Warranty"
                  className="form-input w-48 shrink-0"
                />
                <input
                  type="text"
                  value={option.content}
                  onChange={(e) => handleChangeContent(index, e.target.value)}
                  placeholder="Disclaimer text shown in vehicle description"
                  className="form-input flex-1"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveOption(index)}
                  className="mt-1 p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded focus:outline-none"
                  title="Remove option"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Saving...
            </>
          ) : (
            'Save'
          )}
        </button>
      </div>
    </div>
  );
};

const CustomerImportComponent: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ imported: number; errors: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first.');
      return;
    }

    try {
      setImporting(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);

      const data = await apiService.post<any>('/customers/import/dealercenter/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (data.success) {
        setResult({
          imported: data.imported,
          errors: data.errors,
        });
        setFile(null);
      } else {
        setError(data.message || 'Import failed.');
      }
    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.response?.data?.detail || 'An error occurred during import.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Customer DB Import</h2>
        <p className="text-gray-600 mb-6">
          Import customers from a DealerCenter "All Customers" report CSV file.
        </p>

        <div className="max-w-xl">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
            <UserGroupIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <div className="text-sm text-gray-600 mb-4">
              {file ? (
                <div className="font-medium text-blue-600">{file.name}</div>
              ) : (
                "Select the 'All Customers.csv' file from DealerCenter"
              )}
            </div>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              id="customer-file-upload"
            />
            <label
              htmlFor="customer-file-upload"
              className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <CloudArrowUpIcon className="h-5 w-5 mr-2 text-gray-400" />
              Choose File
            </label>
          </div>

          <div className="mt-4 flex justify-between items-center text-xs text-gray-500">
            <span>DealerCenter "All Customers" Report (CSV format)</span>
            <span>Multi-line blocks supported</span>
          </div>

          <div className="mt-6 flex items-center space-x-4">
            <button
              onClick={handleUpload}
              disabled={!file || importing}
              className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                'Start Import'
              )}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mt-0.5" />
          <div className="text-sm text-red-800 font-medium">{error}</div>
        </div>
      )}

      {result && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <CheckCircleIcon className="h-6 w-6 text-green-500" />
            <h3 className="text-lg font-medium text-green-900">Import Complete</h3>
          </div>
          <div className="text-sm text-green-800 space-y-2">
            <p>
              Successfully imported <strong>{result.imported}</strong> customers into SimpleDealer.
            </p>
            {result.errors.length > 0 && (
              <div className="mt-4">
                <p className="font-semibold text-orange-850">Warnings ({result.errors.length}):</p>
                <ul className="list-disc ml-5 space-y-1 text-orange-800 max-h-40 overflow-y-auto">
                  {result.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-4">
              <a
                href="/customers"
                className="text-green-700 font-semibold hover:text-green-900 underline"
              >
                View all customers &rarr;
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerLeadManagementComponent: React.FC = () => (
  <div>
    <h2 className="text-xl font-semibold text-gray-900 mb-6">Lead Generation</h2>
    <p className="text-gray-600 mb-6">
      Manage how new leads are captured and created, and track lead sources and associated costs to
      evaluate ROI.
    </p>

    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">Activate Advanced duplicate detection</h3>
        </div>
        <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
          <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">
            Add ADF lead provider service name as suffix in lead source name
          </h3>
        </div>
        <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200">
          <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-1" />
        </div>
      </div>
    </div>
  </div>
);

const DealGeneralComponent: React.FC = () => {
  // Separate state for BHPH and Institutional Finance
  const [bhphStandardFees, setBhphStandardFees] = useState([
    { id: 1, label: 'BHPH Setup Fee', value: '199', visible: true },
    { id: 2, label: 'Payment Processing Fee', value: '25', visible: true },
    { id: 3, label: 'GPS Installation Fee', value: '75', visible: true },
    { id: 4, label: 'Account Maintenance Fee', value: '35', visible: true },
  ]);
  const [institutionalStandardFees, setInstitutionalStandardFees] = useState([
    { id: 1, label: 'Temporary Tag Fee', value: '25', visible: true },
    { id: 2, label: 'Tag/Title Transfer Fee', value: '75', visible: true },
    { id: 3, label: 'Inspection Fee', value: '50', visible: true },
    { id: 4, label: 'Lien Recording Fee', value: '15', visible: true },
  ]);

  const [bhphCustomFees, setBhphCustomFees] = useState([
    { id: 1, name: 'Extended Warranty Fee', value: '599', type: 'fixed', visible: true },
    { id: 2, name: 'Credit Life Insurance', value: '299', type: 'fixed', visible: true },
  ]);
  const [institutionalCustomFees, setInstitutionalCustomFees] = useState([
    { id: 1, name: 'Documentation Fee', value: '399', type: 'fixed', visible: true },
    { id: 2, name: 'Processing Fee', value: '199', type: 'fixed', visible: true },
  ]);

  const [isAddingFee, setIsAddingFee] = useState(false);
  const [editingFee, setEditingFee] = useState<number | null>(null);
  const [editingStandardFee, setEditingStandardFee] = useState<number | null>(null);
  const [newFee, setNewFee] = useState({ name: '', value: '', type: 'fixed', visible: true });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Separate Financing Terms Configuration for BHPH and Institutional
  const [bhphDownPaymentType, setBhphDownPaymentType] = useState<'fixed' | 'percentage'>(
    'percentage'
  );
  const [bhphDownPaymentValue, setBhphDownPaymentValue] = useState('20');
  const [bhphInterestRate, setBhphInterestRate] = useState('18.9');
  const [bhphTerm, setBhphTerm] = useState('24');
  const [bhphLoanOriginationFee, setBhphLoanOriginationFee] = useState('399');

  const [institutionalDownPaymentType, setInstitutionalDownPaymentType] = useState<
    'fixed' | 'percentage'
  >('fixed');
  const [institutionalDownPaymentValue, setInstitutionalDownPaymentValue] = useState('1200');
  const [institutionalInterestRate, setInstitutionalInterestRate] = useState('12.5');
  const [institutionalTerm, setInstitutionalTerm] = useState('60');
  const [institutionalLoanOriginationFee, setInstitutionalLoanOriginationFee] = useState('299');

  const [useAutomaticTaxRates, setUseAutomaticTaxRates] = useState(true);
  const [stateTaxRate, setStateTaxRate] = useState('6.5');
  const [countyTaxRate, setCountyTaxRate] = useState('1.5');
  const [localTaxRate, setLocalTaxRate] = useState('0.5');

  // Cash Deals Configuration (simpler - mainly just fees)
  const [cashStandardFees, setCashStandardFees] = useState([
    { id: 1, label: 'Temporary Tag Fee', value: '25', visible: true },
    { id: 2, label: 'DMV Tag/Title Transfer Fee', value: '75', visible: true },
    { id: 3, label: 'Inspection Fee', value: '50', visible: true },
    { id: 4, label: 'Lien Recording Fee', value: '15', visible: false },
  ]);
  const [cashCustomFees, setCashCustomFees] = useState([
    { id: 1, name: 'Documentation Fee', value: '199', type: 'fixed', visible: true },
    { id: 2, name: 'Processing Fee', value: '99', type: 'fixed', visible: true },
  ]);

  // Tab Management for Deal Type Settings
  const [financingTermsTab, setFinancingTermsTab] = useState<'bhph' | 'institutional' | 'cash'>(
    'bhph'
  );
  const [standardFeesTab, setStandardFeesTab] = useState<'bhph' | 'institutional' | 'cash'>('bhph');
  const [customFeesTab, setCustomFeesTab] = useState<'bhph' | 'institutional' | 'cash'>('bhph');

  const applyDealSummarySettings = (settings: any) => {
    // Load BHPH settings
    if (settings?.bhph) {
      if (settings.bhph.standardFees) setBhphStandardFees(settings.bhph.standardFees);
      if (settings.bhph.customFees) setBhphCustomFees(settings.bhph.customFees);
      if (settings.bhph.financingTerms) {
        setBhphDownPaymentType(settings.bhph.financingTerms.downPaymentType);
        setBhphDownPaymentValue(settings.bhph.financingTerms.downPaymentValue);
        if (settings.bhph.financingTerms.interestRate)
          setBhphInterestRate(settings.bhph.financingTerms.interestRate);
        if (settings.bhph.financingTerms.term) setBhphTerm(settings.bhph.financingTerms.term);
        if (settings.bhph.financingTerms.loanOriginationFee)
          setBhphLoanOriginationFee(settings.bhph.financingTerms.loanOriginationFee);
      }
    }

    // Load Institutional settings
    if (settings?.institutional) {
      if (settings.institutional.standardFees)
        setInstitutionalStandardFees(settings.institutional.standardFees);
      if (settings.institutional.customFees)
        setInstitutionalCustomFees(settings.institutional.customFees);
      if (settings.institutional.financingTerms) {
        setInstitutionalDownPaymentType(settings.institutional.financingTerms.downPaymentType);
        setInstitutionalDownPaymentValue(settings.institutional.financingTerms.downPaymentValue);
        if (settings.institutional.financingTerms.interestRate)
          setInstitutionalInterestRate(settings.institutional.financingTerms.interestRate);
        if (settings.institutional.financingTerms.term)
          setInstitutionalTerm(settings.institutional.financingTerms.term);
        if (settings.institutional.financingTerms.loanOriginationFee)
          setInstitutionalLoanOriginationFee(
            settings.institutional.financingTerms.loanOriginationFee
          );
      }
    }

    // Load Cash settings
    if (settings?.cash) {
      if (settings.cash.standardFees) setCashStandardFees(settings.cash.standardFees);
      if (settings.cash.customFees) setCashCustomFees(settings.cash.customFees);
    }
  };

  const applyDealTaxSettings = (settings: DealTaxSettings | null) => {
    if (!settings) return;

    setUseAutomaticTaxRates(settings.use_automatic_tax_rates ?? true);
    setStateTaxRate(
      settings.state_tax_rate !== null && settings.state_tax_rate !== undefined
        ? String(settings.state_tax_rate)
        : ''
    );
    setCountyTaxRate(
      settings.county_surcharge_rate !== null && settings.county_surcharge_rate !== undefined
        ? String(settings.county_surcharge_rate)
        : ''
    );
    setLocalTaxRate(
      settings.local_tax_rate !== null && settings.local_tax_rate !== undefined
        ? String(settings.local_tax_rate)
        : ''
    );
  };

  // Load saved settings on component mount
  useEffect(() => {
    const loadDealSummarySettings = async () => {
      try {
        const settings = await apiService.get('/settings/deal-summary');
        if (settings) {
          applyDealSummarySettings(settings);
          localStorage.setItem('dealSummarySettings', JSON.stringify(settings));
          return;
        }
      } catch (error) {
        console.warn(
          '⚠️ Failed to load deal summary settings from API. Falling back to local storage.',
          error
        );
      }

      const savedSettings = localStorage.getItem('dealSummarySettings');
      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings);
          applyDealSummarySettings(settings);
        } catch (error) {
          console.error('Error loading saved settings:', error);
        }
      }
    };

    loadDealSummarySettings();
  }, []);

  useEffect(() => {
    const loadDealTaxSettings = async () => {
      try {
        const settings = (await apiService.get('/settings/deal')) as DealTaxSettings | null;
        if (settings) {
          applyDealTaxSettings(settings);
          localStorage.setItem('dealTaxSettings', JSON.stringify(settings));
          return;
        }
      } catch (error) {
        console.warn(
          '⚠️ Failed to load deal tax settings from API. Falling back to local storage.',
          error
        );
      }

      const savedSettings = localStorage.getItem('dealTaxSettings');
      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings) as DealTaxSettings;
          applyDealTaxSettings(settings);
        } catch (error) {
          console.error('Error loading saved deal tax settings:', error);
        }
      }
    };

    loadDealTaxSettings();
  }, []);

  // Helper functions to get current tab data
  const getCurrentStandardFees = () => {
    if (standardFeesTab === 'bhph') return bhphStandardFees;
    if (standardFeesTab === 'institutional') return institutionalStandardFees;
    return cashStandardFees;
  };

  const getCurrentCustomFees = () => {
    if (customFeesTab === 'bhph') return bhphCustomFees;
    if (customFeesTab === 'institutional') return institutionalCustomFees;
    return cashCustomFees;
  };

  const getCurrentStandardFeesSetters = () => {
    if (standardFeesTab === 'bhph') return setBhphStandardFees;
    if (standardFeesTab === 'institutional') return setInstitutionalStandardFees;
    return setCashStandardFees;
  };

  const getCurrentCustomFeesSetters = () => {
    if (customFeesTab === 'bhph') return setBhphCustomFees;
    if (customFeesTab === 'institutional') return setInstitutionalCustomFees;
    return setCashCustomFees;
  };

  const addCustomFee = () => {
    if (newFee.name.trim() && newFee.value.trim()) {
      const currentFees = getCurrentCustomFees();
      const setCurrentFees = getCurrentCustomFeesSetters();

      setCurrentFees([
        ...currentFees,
        {
          id: Date.now(),
          name: newFee.name.trim(),
          value: newFee.value.trim(),
          type: newFee.type,
          visible: newFee.visible,
        },
      ]);
      setNewFee({ name: '', value: '', type: 'fixed', visible: true });
      setIsAddingFee(false);
    }
  };

  const updateCustomFee = (id: number, updates: Partial<(typeof bhphCustomFees)[0]>) => {
    const currentFees = getCurrentCustomFees();
    const setCurrentFees = getCurrentCustomFeesSetters();

    setCurrentFees(currentFees.map((fee) => (fee.id === id ? { ...fee, ...updates } : fee)));
    setEditingFee(null);
  };

  const deleteCustomFee = (id: number) => {
    const currentFees = getCurrentCustomFees();
    const setCurrentFees = getCurrentCustomFeesSetters();

    setCurrentFees(currentFees.filter((fee) => fee.id !== id));
  };

  const updateStandardFee = (id: number, updates: Partial<(typeof bhphStandardFees)[0]>) => {
    const currentFees = getCurrentStandardFees();
    const setCurrentFees = getCurrentStandardFeesSetters();

    setCurrentFees(currentFees.map((fee) => (fee.id === id ? { ...fee, ...updates } : fee)));
    setEditingStandardFee(null);
  };

  const toggleFeeVisibility = (id: number, isStandard: boolean) => {
    if (isStandard) {
      const currentFees = getCurrentStandardFees();
      const setCurrentFees = getCurrentStandardFeesSetters();

      setCurrentFees(
        currentFees.map((fee) => (fee.id === id ? { ...fee, visible: !fee.visible } : fee))
      );
    } else {
      const currentFees = getCurrentCustomFees();
      const setCurrentFees = getCurrentCustomFeesSetters();

      setCurrentFees(
        currentFees.map((fee) => (fee.id === id ? { ...fee, visible: !fee.visible } : fee))
      );
    }
  };

  const parseRate = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // Save Settings Handler
  const handleSaveSettings = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      const settingsData = {
        bhph: {
          standardFees: bhphStandardFees,
          customFees: bhphCustomFees,
          financingTerms: {
            downPaymentType: bhphDownPaymentType,
            downPaymentValue: bhphDownPaymentValue,
            interestRate: bhphInterestRate,
            term: bhphTerm,
            loanOriginationFee: bhphLoanOriginationFee,
          },
        },
        institutional: {
          standardFees: institutionalStandardFees,
          customFees: institutionalCustomFees,
          financingTerms: {
            downPaymentType: institutionalDownPaymentType,
            downPaymentValue: institutionalDownPaymentValue,
            interestRate: institutionalInterestRate,
            term: institutionalTerm,
            loanOriginationFee: institutionalLoanOriginationFee,
          },
        },
        cash: {
          standardFees: cashStandardFees,
          customFees: cashCustomFees,
        },
      };

      const dealTaxSettingsData: DealTaxSettings = {
        use_automatic_tax_rates: useAutomaticTaxRates,
        state_tax_rate: parseRate(stateTaxRate),
        county_surcharge_rate: parseRate(countyTaxRate),
        local_tax_rate: parseRate(localTaxRate),
      };

      await Promise.all([
        apiService.post('/settings/deal-summary', settingsData),
        apiService.post('/settings/deal', dealTaxSettingsData),
      ]);

      localStorage.setItem('dealSummarySettings', JSON.stringify(settingsData));
      localStorage.setItem('dealTaxSettings', JSON.stringify(dealTaxSettingsData));

      setSaveMessage('✅ Settings saved successfully!');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage('❌ Failed to save settings. Please try again.');
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to Defaults Handler
  const handleResetToDefaults = () => {
    setBhphStandardFees([
      { id: 1, label: 'BHPH Setup Fee', value: '199', visible: true },
      { id: 2, label: 'Payment Processing Fee', value: '25', visible: true },
      { id: 3, label: 'GPS Installation Fee', value: '75', visible: true },
      { id: 4, label: 'Account Maintenance Fee', value: '35', visible: true },
    ]);
    setInstitutionalStandardFees([
      { id: 1, label: 'Temporary Tag Fee', value: '25', visible: true },
      { id: 2, label: 'Tag/Title Transfer Fee', value: '75', visible: true },
      { id: 3, label: 'Inspection Fee', value: '50', visible: true },
      { id: 4, label: 'Lien Recording Fee', value: '15', visible: true },
    ]);
    setBhphCustomFees([
      { id: 1, name: 'Extended Warranty Fee', value: '599', type: 'fixed', visible: true },
      { id: 2, name: 'Credit Life Insurance', value: '299', type: 'fixed', visible: true },
    ]);
    setInstitutionalCustomFees([
      { id: 1, name: 'Documentation Fee', value: '399', type: 'fixed', visible: true },
      { id: 2, name: 'Processing Fee', value: '199', type: 'fixed', visible: true },
    ]);
    setBhphDownPaymentType('percentage');
    setBhphDownPaymentValue('20');
    setBhphInterestRate('18.9');
    setBhphTerm('24');
    setBhphLoanOriginationFee('399');
    setInstitutionalDownPaymentType('fixed');
    setInstitutionalDownPaymentValue('1200');
    setInstitutionalInterestRate('12.5');
    setInstitutionalTerm('60');
    setInstitutionalLoanOriginationFee('299');
    setCashStandardFees([
      { id: 1, label: 'Temporary Tag Fee', value: '25', visible: true },
      { id: 2, label: 'Tag/Title Transfer Fee', value: '75', visible: true },
      { id: 3, label: 'Documentation Fee', value: '199', visible: true },
    ]);
    setCashCustomFees([
      { id: 1, name: 'Processing Fee', value: '99', type: 'fixed', visible: true },
    ]);
    setUseAutomaticTaxRates(true);
    setStateTaxRate('6.5');
    setCountyTaxRate('1.5');
    setLocalTaxRate('0.5');
    setSaveMessage('🔄 Reset to defaults completed!');
    setTimeout(() => setSaveMessage(null), 3000);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Deal Summary Settings</h2>
        <p className="text-gray-600">
          Configure default values used in deal calculations and customer summaries.
        </p>
      </div>

      {/* Financing Terms Section with Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Financing Terms</h3>
          <span className="text-sm text-blue-600 font-medium">Used in Deal Calculator</span>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setFinancingTermsTab('bhph')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              financingTermsTab === 'bhph'
                ? 'bg-white text-purple-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🤝 BHPH Terms
          </button>
          <button
            onClick={() => setFinancingTermsTab('institutional')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              financingTermsTab === 'institutional'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🏦 Institutional Finance
          </button>
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Interest Rate
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                value={financingTermsTab === 'bhph' ? bhphInterestRate : institutionalInterestRate}
                onChange={(e) => {
                  if (financingTermsTab === 'bhph') {
                    setBhphInterestRate(e.target.value);
                  } else {
                    setInstitutionalInterestRate(e.target.value);
                  }
                }}
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="absolute right-3 top-2 text-gray-500">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {financingTermsTab === 'bhph'
                ? 'BHPH rates typically 15-25% APR'
                : 'Bank/Credit Union rates typically 5-15% APR'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Term (Months)
            </label>
            <select
              value={financingTermsTab === 'bhph' ? bhphTerm : institutionalTerm}
              onChange={(e) => {
                if (financingTermsTab === 'bhph') {
                  setBhphTerm(e.target.value);
                } else {
                  setInstitutionalTerm(e.target.value);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={12}>12 months</option>
              <option value={18}>18 months</option>
              <option value={24}>24 months</option>
              <option value={30}>30 months</option>
              <option value={36}>36 months</option>
              <option value={48}>48 months</option>
              <option value={60}>60 months</option>
              <option value={72}>72 months</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {financingTermsTab === 'bhph'
                ? 'BHPH terms typically 12-36 months'
                : 'Bank terms typically 36-72 months'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {financingTermsTab === 'bhph' ? 'BHPH Processing Fee' : 'Loan Origination Fee'}
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                $
              </span>
              <input
                type="number"
                value={
                  financingTermsTab === 'bhph'
                    ? bhphLoanOriginationFee
                    : institutionalLoanOriginationFee
                }
                onChange={(e) => {
                  if (financingTermsTab === 'bhph') {
                    setBhphLoanOriginationFee(e.target.value);
                  } else {
                    setInstitutionalLoanOriginationFee(e.target.value);
                  }
                }}
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={financingTermsTab === 'bhph' ? '399' : '299'}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {financingTermsTab === 'bhph'
                ? 'Standard processing fee for BHPH deals'
                : 'Bank/lender origination fee'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Down Payment
            </label>
            <div className="space-y-3">
              {/* Value Input */}
              <div className="flex items-center space-x-2">
                {(financingTermsTab === 'bhph'
                  ? bhphDownPaymentType
                  : institutionalDownPaymentType) === 'fixed' && (
                  <span className="text-gray-500">$</span>
                )}
                <input
                  type="number"
                  value={
                    financingTermsTab === 'bhph'
                      ? bhphDownPaymentValue
                      : institutionalDownPaymentValue
                  }
                  onChange={(e) => {
                    if (financingTermsTab === 'bhph') {
                      setBhphDownPaymentValue(e.target.value);
                    } else {
                      setInstitutionalDownPaymentValue(e.target.value);
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={financingTermsTab === 'bhph' ? '20' : '1000'}
                />
                {(financingTermsTab === 'bhph'
                  ? bhphDownPaymentType
                  : institutionalDownPaymentType) === 'percentage' && (
                  <span className="text-gray-500">%</span>
                )}
              </div>

              {/* Type Selection */}
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name={`downPaymentType-${financingTermsTab}`}
                    value="fixed"
                    checked={
                      (financingTermsTab === 'bhph'
                        ? bhphDownPaymentType
                        : institutionalDownPaymentType) === 'fixed'
                    }
                    onChange={(e) => {
                      if (financingTermsTab === 'bhph') {
                        setBhphDownPaymentType(e.target.value as 'fixed' | 'percentage');
                      } else {
                        setInstitutionalDownPaymentType(e.target.value as 'fixed' | 'percentage');
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Fixed Amount ($)</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="radio"
                    name={`downPaymentType-${financingTermsTab}`}
                    value="percentage"
                    checked={
                      (financingTermsTab === 'bhph'
                        ? bhphDownPaymentType
                        : institutionalDownPaymentType) === 'percentage'
                    }
                    onChange={(e) => {
                      if (financingTermsTab === 'bhph') {
                        setBhphDownPaymentType(e.target.value as 'fixed' | 'percentage');
                      } else {
                        setInstitutionalDownPaymentType(e.target.value as 'fixed' | 'percentage');
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Percentage (%)</span>
                </label>
              </div>

              <p className="text-xs text-gray-500">
                {financingTermsTab === 'bhph'
                  ? 'BHPH typically requires higher down payments (15-25%)'
                  : 'Bank financing may require lower down payments (0-15%)'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tax Settings Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Tax Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Use Automated Tax Rates</h4>
              <p className="text-sm text-gray-500">
                Automatically calculate tax based on customer location
              </p>
            </div>
            <button
              type="button"
              onClick={() => setUseAutomaticTaxRates((prev) => !prev)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full ${useAutomaticTaxRates ? 'bg-blue-600' : 'bg-gray-200'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${useAutomaticTaxRates ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State Tax Rate</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={stateTaxRate}
                  onChange={(e) => setStateTaxRate(e.target.value)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="absolute right-3 top-2 text-gray-500">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                County Tax Rate
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={countyTaxRate}
                  onChange={(e) => setCountyTaxRate(e.target.value)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="absolute right-3 top-2 text-gray-500">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Local Tax Rate</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={localTaxRate}
                  onChange={(e) => setLocalTaxRate(e.target.value)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="absolute right-3 top-2 text-gray-500">%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Standard Fees Section with Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Standard Fees</h3>
          <span className="text-sm text-gray-500">
            Click labels to edit • Toggle a fee off to stop charging it
          </span>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setStandardFeesTab('bhph')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              standardFeesTab === 'bhph'
                ? 'bg-white text-purple-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🤝 BHPH Fees
          </button>
          <button
            onClick={() => setStandardFeesTab('institutional')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              standardFeesTab === 'institutional'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🏦 Institutional Fees
          </button>
          <button
            onClick={() => setStandardFeesTab('cash')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              standardFeesTab === 'cash'
                ? 'bg-white text-green-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            💰 Cash Deal Fees
          </button>
        </div>

        <div className="space-y-4">
          {getCurrentStandardFees().map((fee) => (
            <div
              key={fee.id}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-4 flex-1">
                {/* Toggle Switch */}
                <button
                  onClick={() => toggleFeeVisibility(fee.id, true)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    fee.visible ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      fee.visible ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>

                {/* Editable Label */}
                {editingStandardFee === fee.id ? (
                  <input
                    type="text"
                    value={fee.label}
                    onChange={(e) => updateStandardFee(fee.id, { label: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onBlur={() => setEditingStandardFee(null)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setEditingStandardFee(null);
                      if (e.key === 'Escape') setEditingStandardFee(null);
                    }}
                    autoFocus
                  />
                ) : (
                  <button
                    onClick={() => setEditingStandardFee(fee.id)}
                    className="flex-1 text-left px-3 py-2 text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    {fee.label}
                  </button>
                )}

                {/* Value Input */}
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="number"
                    value={fee.value}
                    onChange={(e) => updateStandardFee(fee.id, { value: e.target.value })}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Status Indicators */}
              <div className="flex items-center space-x-2 ml-4">
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    fee.visible ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {fee.visible ? 'Visible in Deal Summary' : 'Hidden'}
                </span>
                <button
                  onClick={() => setEditingStandardFee(fee.id)}
                  className="p-2 text-gray-400 hover:text-blue-600"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>Tip:</strong> Toggle a fee off to remove it from this deal type — it will not be
            charged and will not appear on the deal or the contract. Toggle it on to charge it.
          </p>
        </div>
      </div>

      {/* Custom Fees Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Custom Fees</h3>
          <button
            onClick={() => setIsAddingFee(true)}
            className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Custom Fee
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setCustomFeesTab('bhph')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              customFeesTab === 'bhph'
                ? 'bg-white text-purple-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🤝 BHPH Custom Fees
          </button>
          <button
            onClick={() => setCustomFeesTab('institutional')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              customFeesTab === 'institutional'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🏦 Institutional Custom Fees
          </button>
          <button
            onClick={() => setCustomFeesTab('cash')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              customFeesTab === 'cash'
                ? 'bg-white text-green-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            💰 Cash Deal Custom Fees
          </button>
        </div>

        <div className="space-y-3">
          {getCurrentCustomFees().map((fee) => (
            <div
              key={fee.id}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              {editingFee === fee.id ? (
                <div className="flex items-center space-x-4 flex-1">
                  <input
                    type="text"
                    value={fee.name}
                    onChange={(e) => updateCustomFee(fee.id, { name: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Fee name"
                  />
                  <div className="flex items-center space-x-2">
                    {fee.type === 'fixed' && <span className="text-gray-500">$</span>}
                    <input
                      type="number"
                      value={fee.value}
                      onChange={(e) => updateCustomFee(fee.id, { value: e.target.value })}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Value"
                    />
                    {fee.type === 'percentage' && <span className="text-gray-500">%</span>}
                  </div>
                  <select
                    value={fee.type}
                    onChange={(e) =>
                      updateCustomFee(fee.id, { type: e.target.value as 'fixed' | 'percentage' })
                    }
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="fixed">Fixed</option>
                    <option value="percentage">Percentage</option>
                  </select>
                  <button
                    onClick={() => setEditingFee(null)}
                    className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Save
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center space-x-4 flex-1">
                    {/* Toggle Switch for Custom Fees */}
                    <button
                      onClick={() => toggleFeeVisibility(fee.id, false)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        fee.visible ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          fee.visible ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>

                    <span className="font-medium text-gray-900">{fee.name}</span>
                    <span className="text-gray-600">
                      {fee.type === 'fixed' ? `$${fee.value}` : `${fee.value}%`}
                    </span>
                    <span className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-full">
                      {fee.type === 'fixed' ? 'Fixed Amount' : 'Percentage'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        fee.visible ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {fee.visible ? 'Visible' : 'Hidden'}
                    </span>
                    <button
                      onClick={() => setEditingFee(fee.id)}
                      className="p-2 text-gray-400 hover:text-blue-600"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deleteCustomFee(fee.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}

          {/* Add New Fee Form */}
          {isAddingFee && (
            <div className="p-4 bg-blue-50 rounded-lg border-2 border-dashed border-blue-300">
              <div className="flex items-center space-x-4">
                <input
                  type="text"
                  value={newFee.name}
                  onChange={(e) => setNewFee({ ...newFee, name: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter fee name (e.g., Extended Warranty Fee)"
                />
                <div className="flex items-center space-x-2">
                  {newFee.type === 'fixed' && <span className="text-gray-500">$</span>}
                  <input
                    type="number"
                    value={newFee.value}
                    onChange={(e) => setNewFee({ ...newFee, value: e.target.value })}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Value"
                  />
                  {newFee.type === 'percentage' && <span className="text-gray-500">%</span>}
                </div>
                <select
                  value={newFee.type}
                  onChange={(e) =>
                    setNewFee({ ...newFee, type: e.target.value as 'fixed' | 'percentage' })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="fixed">Fixed</option>
                  <option value="percentage">Percentage</option>
                </select>

                {/* Visibility Toggle for New Fee */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setNewFee({ ...newFee, visible: !newFee.visible })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      newFee.visible ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        newFee.visible ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-sm text-gray-700">Visible</span>
                </div>

                <button
                  onClick={addCustomFee}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setIsAddingFee(false);
                    setNewFee({ name: '', value: '', type: 'fixed', visible: true });
                  }}
                  className="px-4 py-2 text-sm bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {getCurrentCustomFees().length === 0 && !isAddingFee && (
            <div className="text-center py-8 text-gray-500">
              <CurrencyDollarIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No custom fees added yet.</p>
              <p className="text-sm">Click "Add Custom Fee" to create dealer-specific fees.</p>
            </div>
          )}
        </div>
      </div>

      {/* Integration Settings Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Deal Summary Options</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Allow Vehicle Selection from Inventory</h4>
              <p className="text-sm text-gray-500">
                Enable dealers to select vehicles from existing inventory
              </p>
            </div>
            <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Allow Customer Selection from CRM</h4>
              <p className="text-sm text-gray-500">
                Enable dealers to select existing customers from management system
              </p>
            </div>
            <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Enable SMS Deal Summaries</h4>
              <p className="text-sm text-gray-500">
                Allow sending deal summaries via text message to customers
              </p>
            </div>
            <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Enable Email Deal Summaries</h4>
              <p className="text-sm text-gray-500">
                Allow sending deal summaries via email to customers
              </p>
            </div>
            <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div
          className={`text-center p-3 rounded-lg font-medium ${
            saveMessage.includes('✅')
              ? 'bg-green-50 text-green-800 border border-green-200'
              : saveMessage.includes('❌')
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-blue-50 text-blue-800 border border-blue-200'
          }`}
        >
          {saveMessage}
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={handleResetToDefaults}
          disabled={isSaving}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          🔄 Reset to Defaults
        </button>
        <button
          onClick={handleSaveSettings}
          disabled={isSaving}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Saving...
            </>
          ) : (
            <>💾 Save Settings</>
          )}
        </button>
      </div>
    </div>
  );
};

interface BHPHGeneralFormState {
  bhph_company_name: string;
  bhph_display_mode: string;
  late_days_threshold: string;
  late_fee_amount: string;
  late_charge_rate: string;
  bhph_badge_image_url: string;
}

const defaultBHPHGeneralFormState: BHPHGeneralFormState = {
  bhph_company_name: '',
  bhph_display_mode: 'badge_and_terms',
  late_days_threshold: '10',
  late_fee_amount: '0.00',
  late_charge_rate: '5.00',
  bhph_badge_image_url: '',
};

const BHPHGeneralComponent: React.FC = () => {
  const [form, setForm] = useState<BHPHGeneralFormState>(defaultBHPHGeneralFormState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const response = await apiService.get<Partial<BHPHGeneralFormState> | null>(
          '/settings/bhph'
        );
        if (response) {
          setForm((prev) => ({
            ...prev,
            bhph_company_name: (response as any).bhph_company_name ?? prev.bhph_company_name,
            bhph_display_mode: (response as any).bhph_display_mode ?? prev.bhph_display_mode,
            late_days_threshold: String(
              (response as any).late_days_threshold ?? prev.late_days_threshold
            ),
            late_fee_amount: String((response as any).late_fee_amount ?? prev.late_fee_amount),
            late_charge_rate: String((response as any).late_charge_rate ?? prev.late_charge_rate),
            bhph_badge_image_url:
              (response as any).bhph_badge_image_url ?? prev.bhph_badge_image_url,
          }));
        }
      } catch (error) {
        console.error('Failed to load BHPH settings:', error);
        setStatusMessage('Unable to load BHPH settings.');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const setField = (field: keyof BHPHGeneralFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setStatusMessage(null);

      await apiService.post('/settings/bhph', {
        bhph_company_name: form.bhph_company_name,
        bhph_display_mode: form.bhph_display_mode,
        late_days_threshold: parseInt(form.late_days_threshold, 10) || 10,
        late_fee_amount: parseFloat(form.late_fee_amount) || 0,
        late_charge_rate: parseFloat(form.late_charge_rate) || 5,
        bhph_badge_image_url: form.bhph_badge_image_url,
      });

      setStatusMessage('BHPH settings saved successfully.');
    } catch (error) {
      console.error('Failed to save BHPH settings:', error);
      setStatusMessage('Failed to save BHPH settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleBadgeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const backendUrl = apiService.getBaseUrl();
      const res = await fetch(`${backendUrl}/api/v1/settings/bhph/badge-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setField('bhph_badge_image_url', data.bhph_badge_image_url);
    } catch (error) {
      console.error('Failed to upload badge image:', error);
      setStatusMessage('Failed to upload badge image.');
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">BHPH Company Settings</h2>

      {loading ? (
        <div className="text-sm text-gray-500">Loading BHPH settings...</div>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Company Name</h3>
              <input
                type="text"
                value={form.bhph_company_name}
                onChange={(e) => setField('bhph_company_name', e.target.value)}
                placeholder="Enter company name"
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="ml-4 flex-shrink-0 text-center">
              {form.bhph_badge_image_url ? (
                <img
                  src={apiService.getFileUrl(form.bhph_badge_image_url)}
                  alt="BHPH badge"
                  className="w-24 h-16 object-contain border border-gray-200 rounded-lg mb-1"
                />
              ) : (
                <div className="w-24 h-16 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center mb-1">
                  <span className="text-xs text-gray-500">No badge image</span>
                </div>
              )}
              <label className="text-xs text-blue-600 cursor-pointer hover:underline">
                Upload Badge Image
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleBadgeUpload}
                />
              </label>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-4">Late Charge Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Late if days past due is greater than
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.late_days_threshold}
                    onChange={(e) => setField('late_days_threshold', e.target.value)}
                    className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md"
                  />
                  <span className="absolute right-3 top-2 text-gray-500">days</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Late Fee</label>
                <input
                  type="text"
                  value={form.late_fee_amount}
                  onChange={(e) => setField('late_fee_amount', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Late Charge Rate
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.late_charge_rate}
                    onChange={(e) => setField('late_charge_rate', e.target.value)}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md"
                  />
                  <span className="absolute right-3 top-2 text-gray-500">%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Public Website — BHPH Vehicle Display */}
          <div className="space-y-3 mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700">
              Public Website — BHPH Vehicle Display
            </h4>
            <p className="text-xs text-gray-500">
              Choose how BHPH-eligible vehicles appear to customers on your website.
            </p>
            {[
              {
                value: 'badge_only',
                label: 'Badge Only',
                desc: 'Shows a financing badge, no payment details',
              },
              {
                value: 'badge_and_terms',
                label: 'Badge + Terms',
                desc: 'Shows badge with monthly payment and down payment info',
              },
              {
                value: 'full_calculator',
                label: 'Full Calculator',
                desc: 'Shows badge, terms, and an interactive payment calculator (coming soon)',
              },
            ].map((opt) => (
              <label
                key={opt.value}
                className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <input
                  type="radio"
                  name="bhph_display_mode"
                  value={opt.value}
                  checked={form.bhph_display_mode === opt.value}
                  onChange={(e) => setField('bhph_display_mode', e.target.value)}
                  className="h-4 w-4 text-blue-600 mt-0.5"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">{opt.label}</span>
                  <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>

          {statusMessage && (
            <p
              className={`text-sm ${statusMessage.includes('successfully') ? 'text-green-600' : 'text-red-600'}`}
            >
              {statusMessage}
            </p>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save BHPH Settings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AllNotificationsComponent: React.FC = () => (
  <div>
    <h2 className="text-xl font-semibold text-gray-900 mb-4">All Notifications</h2>
    <p className="text-gray-600 mb-8">
      Notifications are designed to provide updates, reminders, and information about
      vehicle-related services or offers.
    </p>

    <div className="space-y-3">
      {[
        'CRM',
        'Deal',
        'Westlake Financial',
        'Inventory',
        'BHPH',
        'Service',
        'Carzing/Deal Driver',
        'Payment',
      ].map((item, index) => (
        <div
          key={index}
          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <span className="font-medium text-gray-900">{item}</span>
          <ChevronRightIcon className="h-5 w-5 text-gray-400" />
        </div>
      ))}
    </div>
  </div>
);

export default SettingsPage;
